'use client';

import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import Image from '@/helpers/Image';

import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';
import { CTAButton } from '@/shadcn/ui/button';
import waveDots from '@/public/fabio-affiliate/noise.jpg';
import horse from '@/public/fabio-affiliate/horseman.png';
import signature from '@/public/fabio-affiliate/signature.svg';
import GradualBlur from '@/shadcn/GradualBlur';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function FinalCTASection({
  data,
}: {
  data: PageFabioAffiliateSchema;
}) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const cards = data?.testimonialsItems ?? [];
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [stackOffsetPx, setStackOffsetPx] = useState(40 * 8);
  const [isMobile, setIsMobile] = useState(false);

  const cardEls = useRef<(HTMLElement | null)[]>([]);
  const [cardHeightsPx, setCardHeightsPx] = useState<number[]>([]);

  if (!data?.testimonialsHeading && !cards.length) return null;
  const ratingLabel =
    cards.length > 0
      ? `${String(currentCardIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`
      : null;
  return (
    <section
      ref={sectionRef}
      className="overflow-hidden relative w-ful pt-[8rem] bg-[#F4F4F4] lg:pt-[15rem]"
    >
      <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t to-transparent z-[9] h-[22.25rem] from-[#F4F4F4]" />
      <GradualBlur
        target="parent"
        position="bottom"
        height="22.25rem"
        strength={2}
        divCount={5}
        curve="bezier"
        exponential
        opacity={1}
        zIndex={7}
      />
      <Image
        src={waveDots}
        alt=""
        className="absolute inset-0 object-cover !w-full !h-full mix-blend-exclusion opacity-10 pointer-events-none"
      />
      {data?.heroVersion === 'default' ? (
        <Image
          src={signature}
          alt=""
          className="absolute bottom-[42%] left-1/2 -translate-x-1/2 lg:bottom-1/3 pointer-events-none"
        />
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="215"
          height="53"
          fill="none"
          viewBox="0 0 215 53"
          className="lg:w-[100rem] w-[40rem] h-auto pointer-events-none absolute bottom-[42%] left-1/2 -translate-x-1/2 lg:bottom-[20%] "
        >
          <g filter="url(#filter0_i_2115_4115)">
            <path
              fill="#fff"
              fillOpacity="0.5"
              d="M14.425 18.511h.444q.284.06.284.303 0 .608-1.315 3.642h34.695l.728.162v.425q0 .444-2.65.445h-.566l-.425-.304H13.413q0 .243-11.228 26.38.303 0 .303.426-.91 2.023-1.76 2.023Q0 51.932 0 51.588l12.118-28.404H2.772q-.284 0-.284-.303.162-.425.89-.425h8.862q.242 0 1.901-3.783zm5.805-6.757q.627.303.728.566v.304q-.162.89-6.13 15.72 0 .1-.162.606 1.76-2.913 3.379-2.913h.425q.324 0 .445.728a24 24 0 0 0-1.032 3.035q.95-.567 3.217-2.752h.586q.264.02.263.304v.263q-2.427 2.65-3.641 3.216h-1.295q-.222 0-.425-.424V29.8q0-.668.729-2.307h-.162q-2.023 3.5-3.055 3.5h-.283q-.789 0-.87-.465v-.121q.87-3.176 6.696-17.621l-.142-.729q.445-.303.729-.303m4.247 13.737h.263q1.194.1 1.194 1.011 0 .81-3.217 1.76-.728 1.538-.728 2.023h.142q1.76-.141 4.794-2.751h.466q.263.02.263.263v.303q-2.913 2.913-5.099 2.913h-.85q-1.03 0-1.03-.87 0-.768.727-1.74l-.141-.303v-.303q.303 0 2.185-2.023zm-.586 1.456h.121l.607-.607q-.668.304-.728.607m18.347-1.598q.729.02.729.263 0 1.092-.729 4.107v.141h.142q1.254-2.79 1.881-2.791h.466q.546.728.85.728.08 0 1.76-.567l.566.142v.587a4.25 4.25 0 0 1-2.145.91l-1.315-.162q-1.274 2.61-2.508 2.61h-.587q-.202 0-.425-.445v-.89l.162-.85v-.142h-.162q-1.092 2.914-2.185 2.914h-.445q-.282-.101-.586-.729.727-4.673 1.456-4.673h.466q.263 0 .263.445 0 .405-.425 2.043v.142h.162q1.476-3.783 2.61-3.783m8.051.627q.77 0 1.012 1.457 0 .12-.425 2.063 1.214-.444 2.751-1.901.285-.162.587-.162.303.162.445.162v.303q-2.023 2.326-4.37 2.326 0 .547-1.173 1.316H47.8q-.567-.284-.567-1.032v-.162q0-2.55 2.185-4.208zm-.303 1.335v.587h.162v-.587zm-.142.729v.586h.142v-.586zm-1.315.85-.141 1.456v.465h.141q.546-.303.728-.89-.08 0-.728-1.032m7.404-5.16h.141q1.315 0 1.315 1.457-.425.95-.425 1.315.85.567 1.032.567v.607q0 .282-.465 1.456h.162q2.913-2.064 3.338-2.064h.142q.303.061.303.304v.303q-2.448 2.307-3.945 2.307h-.263q-1.032 0-1.032-.85.425-1.395.425-1.598l-.283-.162q-2.752 4.794-3.945 7.445h-.87q-.283 0-.283-.304 1.335-3.196 4.066-8.01-.425-.001-.728-1.316v-.566q.526-.89 1.315-.89m-.122 1.316.263.141v-.425q-.263.04-.263.284M73.897 0h.303q.425 0 .425.587-.728 2.145-3.075 5.664 0 .345-2.61 5.827-7.586 16.731-7.708 19.967.122.284.122.425h.162q.748 0 4.936-4.936.283-.162.607-.162h.121q.303.04.304.303v.284q-3.783 5.4-6.393 5.401-1.032 0-1.032-1.032v-.586q0-3.966 13.11-31.62zm6.736 8.295h.566q.303 0 .304.445L68.98 37.993q.141.303.141.465l-.445.263h-.121q-.79 0-.89-.424 0-.749 5.098-12.382-1.295 1.396-1.295 2.347-.708 0-2.185 1.457-1.072.728-1.618.728h-1.032q-.263 0-.263-.303v-.162q.081-1.882 2.751-4.795l.729-.142h.162q.546 0 1.294.729h.162q2.994-4.917 4.633-8.436 2.488-4.835 4.228-8.902zm-12.806 21.12a6.3 6.3 0 0 0 2.448-2.346v-.122q-.142-.87-.425-.87-1.64 1.78-2.023 3.338m22.697-3.864q1.295.224 1.295 1.032-.182 1.011-.567 1.012h-.728q-.283 0-.283-.284.282-.485.283-.87-1.862 0-3.338 4.208.141.304.141.425h.425q1.254 0 5.402-3.48.242-.16.587-.161h.141q.283.06.283.303v.304q-4.107 3.924-6.838 3.924-1.456-.243-1.456-1.315 1.638-5.097 4.653-5.098M106.748 0h.303q.425 0 .425.587-.728 2.145-3.075 5.664 0 .345-2.61 5.827-7.585 16.731-7.707 19.967.12.284.121.425h.162q.748 0 4.936-4.936.283-.162.607-.162h.121q.304.04.304.303v.284q-3.783 5.4-6.393 5.401-1.032 0-1.032-1.032v-.586q0-3.966 13.11-31.62zm-1.235 24.277h.445q.283.04.283.303v.122q-1.74 4.005-1.739 5.26h.121q1.578-.83 4.107-2.61h.141q.243.141.587.141v.425q-3.318 2.913-4.956 2.913h-.446q-.89 0-.89-.87V29.8q0-.992.304-1.578v-.142h-.142q-1.274 1.72-2.61 1.72h-.303q-.77 0-.89-.425v-.425q0-1.72 2.185-3.055.789-.465 1.456-.465.648.162 1.032.162 1.093-1.315 1.315-1.315m-4.653 4.531h.121q2.185-1.86 2.185-2.184l-.263-.162h-.303q-1.132.585-1.74 2.185zm9.811-4.106h.566q1.032 0 1.032 1.031 0 .486-1.901 2.044.485.546.869 2.326 1.032 0 4.37-2.61h.587q.303.04.303.284v.141q-2.528 2.367-5.401 3.217-.424 1.153-1.74 1.153h-.87v-.566q.486-.729 1.598-1.315 0-.81-1.294-3.359v-.424q.444-1.922 1.881-1.922m-.728 2.063q.91-.547 1.153-1.335h-.122q-1.03.465-1.031 1.335m7.747-2.063h.567q1.032 0 1.031 1.031 0 .486-1.901 2.044.484.546.87 2.326 1.032 0 4.369-2.61h.587q.304.04.304.284v.141q-2.53 2.367-5.402 3.217-.425 1.153-1.74 1.153h-.87v-.566q.486-.729 1.599-1.315 0-.81-1.295-3.359v-.424q.446-1.922 1.881-1.922m-.728 2.063q.91-.547 1.153-1.335h-.121q-1.032.465-1.032 1.335m20.148-1.274h.263q1.194.1 1.194 1.011 0 .81-3.217 1.76-.728 1.538-.728 2.023h.141q1.76-.141 4.795-2.751h.465q.263.02.263.263v.303q-2.913 2.913-5.098 2.913h-.85q-1.03 0-1.031-.87 0-.768.728-1.74l-.142-.303v-.303q.304 0 2.185-2.023zm-.587 1.456h.122l.607-.607q-.668.304-.729.607m16.953-18.652h.566q.303 0 .304.445l-12.523 29.253q.141.303.141.465l-.445.263h-.121q-.79 0-.89-.424 0-.749 5.098-12.382-1.295 1.396-1.295 2.347-.708 0-2.185 1.457-1.071.728-1.618.728h-1.032q-.263 0-.263-.303v-.162q.081-1.882 2.751-4.795l.729-.142h.162q.546 0 1.294.729h.162q2.994-4.917 4.633-8.436 2.488-4.835 4.228-8.902zm-12.806 21.12a6.3 6.3 0 0 0 2.448-2.346v-.122q-.142-.87-.425-.87-1.64 1.78-2.023 3.338m14.1-5.32q.263.162.424.162v.262q0 .648-1.618 4.836 3.256-2.347 3.945-2.347h.425q.303.04.303.303v.122q-.262.465-3.358 2.185-2.468 1.74-2.468 2.913-5.361 13.575-7.425 13.575h-.162q-1.294-.122-1.294-1.194.748-4.734 5.098-11.046.849-1.153 3.216-3.358l.85-2.61q-1.881 1.72-3.034 1.72h-.162q-1.032 0-1.032-.992v-.303q1.133-3.075 3.641-3.075.729 0 .729.465 1.375-1.618 1.922-1.618m-4.977 4.37v.303q.849-.323 2.913-2.792h-.283q-1.639 0-2.63 2.489m-4.957 16.609q1.094 0 5.685-11.35.142-.303.142-.465-2.63 3.197-4.208 6.717-1.619 3.54-1.619 5.098m15.962-19.583h.263q1.193.1 1.193 1.011 0 .81-3.217 1.76-.728 1.538-.728 2.023h.142q1.76-.141 4.794-2.751h.466q.263.02.263.263v.303q-2.913 2.913-5.098 2.913h-.85q-1.032 0-1.032-.87 0-.768.728-1.74l-.141-.303v-.303q.303 0 2.185-2.023zm-.587 1.456h.121l.607-.607q-.668.304-.728.607m19.055-8.436h.446q.282.06.283.303 0 .608-1.315 3.642h34.695l.729.162v.425q0 .444-2.651.445h-.566l-.425-.304h-32.207q0 .243-11.228 26.38.303 0 .303.426-.91 2.023-1.76 2.023-.728-.081-.728-.425l12.118-28.404h-9.346q-.284 0-.284-.303.162-.425.891-.425h8.861q.242 0 1.901-3.783zm2.063 6.98h.263q1.194.1 1.194 1.011 0 .81-3.217 1.76-.728 1.538-.728 2.023h.141q1.76-.141 4.795-2.751h.465q.263.02.263.263v.303q-2.913 2.913-5.098 2.913h-.849q-1.032 0-1.032-.87 0-.768.728-1.74l-.141-.303v-.303q.303 0 2.184-2.023zm-.587 1.456h.122l.607-.607q-.667.304-.729.607m8.982-2.67h.445q.283.04.283.303v.122q-1.74 4.005-1.74 5.26h.122q1.578-.83 4.107-2.61h.141q.243.141.587.141v.425q-3.318 2.913-4.957 2.913h-.445q-.89 0-.89-.87V29.8q0-.992.304-1.578v-.142h-.142q-1.274 1.72-2.61 1.72h-.303q-.769 0-.89-.425v-.425q0-1.72 2.185-3.055.789-.465 1.456-.465.648.162 1.032.162 1.093-1.315 1.315-1.315m-4.653 4.531h.121q2.185-1.86 2.185-2.184l-.263-.162h-.303q-1.133.585-1.74 2.185zm15.091-4.41h.263q1.032 0 1.032 1.012 0 .323-1.032 4.208l.162.303q.486 0 3.642-2.488h.303q.263.162.425.162v.303q-2.428 2.751-4.673 2.751-.91 0-1.012-1.153l1.012-4.248q-.547.12-3.035 6.13l-.607.586h-1.153q0-.163-.162-.424l1.457-5.099q-1.64 2.752-2.913 3.905h-1.295v-.567q0-1.497 1.153-2.468.728 0 .728.425 1.78-2.751 2.489-2.751h.141q.89 0 .89.991v.728h.122q.85-2.306 2.063-2.306"
            ></path>
          </g>
          <defs>
            <filter
              id="filter0_i_2115_4115"
              width="214.104"
              height="55.064"
              x="0"
              y="0"
              colorInterpolationFilters="sRGB"
              filterUnits="userSpaceOnUse"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
              <feBlend
                in="SourceGraphic"
                in2="BackgroundImageFix"
                result="shape"
              ></feBlend>
              <feColorMatrix
                in="SourceAlpha"
                result="hardAlpha"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              ></feColorMatrix>
              <feOffset dy="3.051"></feOffset>
              <feGaussianBlur stdDeviation="1.526"></feGaussianBlur>
              <feComposite
                in2="hardAlpha"
                k2="-1"
                k3="1"
                operator="arithmetic"
              ></feComposite>
              <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"></feColorMatrix>
              <feBlend
                in2="shape"
                result="effect1_innerShadow_2115_4115"
              ></feBlend>
            </filter>
          </defs>
        </svg>
      )}

      <div className="h-full">
        <div className="mx-auto flex flex-col w-[min(147.5rem,calc(100%-4rem))] items-center justify-between gap-[6rem] lg:gap-[13rem] pb-[60rem] lg:pb-[67rem]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col items-center"
            style={{ willChange: 'transform' }}
          >
            {data?.finalCtaChip && (
              <p className="italic text-center text-black mb-[1rem] font-blacker text-[2rem] leading-[1.5]">
                {data.finalCtaChip}
              </p>
            )}

            {data?.finalCtaHeading && (
              <div className="lg:w-full richtext w-[80%] prose-p:w-[80%] lg:prose-p:w-auto prose-em:font-normal prose-headings:bg-gradient-to-b prose-headings:from-[#939393] prose-headings:to-black prose-headings:via-black prose-headings:bg-clip-text prose-headings:text-transparent prose-headings:tracking-[-0.3rem] prose-headings:text-[4.75rem] lg:prose-headings:tracking-[-0.45rem] lg:prose-headings:text-[7rem]">
                <PortableText
                  value={data.finalCtaHeading}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.finalCtaButton && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.5 }}
                className="w-full mt-[3rem] lg:w-fit"
              >
                <CTAButton data={data.finalCtaButton} />
              </motion.div>
            )}
          </motion.div>

          <div className="absolute bottom-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease, delay: 0.3 }}
              className="w-full h-full"
            >
              <Image src={horse} alt="" className="pointer-events-none" />
            </motion.div>
          </div>

          {/* {data.finalCtaImage && ( */}
          {/*   <div */}
          {/*     className="absolute left-1/2 -translate-x-1/2 lg:bottom-[-15.75rem] bottom-[-2rem] lg:w-[83rem] w-[50.25rem] h-[50.25rem] lg:h-[80.875rem] pointer-events-none lg:[clip-path:inset(0_0_19%_0)] z-[5]" */}
          {/*     aria-hidden="true"> */}
          {/*     <motion.div */}
          {/*       initial={{ opacity: 0, scale: 0.97 }} */}
          {/*       animate={{ opacity: 1, scale: 1 }} */}
          {/*       transition={{ duration: 1.4, ease, delay: 0.3 }} */}
          {/*       className="w-full h-full"> */}
          {/*       <GroqImage */}
          {/*         src={data.finalCtaImage} */}
          {/*         alt={data.finalCtaImage.name || ""} */}
          {/*         className="object-contain object-top" */}
          {/*       /> */}
          {/*     </motion.div> */}
          {/*   </div> */}
          {/* )} */}
        </div>
      </div>
    </section>
  );
}
